// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/system";
import { useState } from "react";
import { IAvailableProject } from "Utils/typings/state/folder";
import CloseIcon from '@mui/icons-material/Close';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import { getAllFoldersThunk, moveBulkProjectsToFolderThunk } from "Features/folderList/folderListThunk";
import { useAppDispatch } from "Hooks";
import { InvalidateMapCoordinatesAction } from "Features/projectList/projectListActions";
import { useParams } from "react-router-dom";
import stylesDef from "./styles";

interface MoveProjectBulkDialogProps {
  open: boolean;
  onClose: () => void;
  availableProjects: IAvailableProject[];
  loadingAvailableProjects: boolean;
}

const MoveProjectBulkDialog = ({ open, onClose, availableProjects, loadingAvailableProjects }: MoveProjectBulkDialogProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const { folderId } = useParams();
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [moveFailed, setMoveFailed] = useState<boolean>(false);

  // The folders' counts follow a move; after a refused one, the refreshed list lets the page behind show a folder
  // deleted meanwhile as gone.
  const refreshFolders = async () => {
    try {
      await dispatch(getAllFoldersThunk());
    } catch (error) {
      // The counts on screen stay as they were until the folders are next read.
    }
  };

  const handleMoveProjects = async () => {
    setLoading(true);
    setMoveFailed(false);
    try {
      await dispatch(moveBulkProjectsToFolderThunk(selectedProjects, folderId as string));
    } catch (error) {
      setMoveFailed(true);
      setLoading(false);
      await refreshFolders();
      return;
    }
    await refreshFolders();
    // The dashboard's cached set still shows these projects as unassigned; it refetches next time.
    dispatch(InvalidateMapCoordinatesAction());
    onClose();
    setLoading(false);
  };

  const handleSelectProject = (projectId: string) => {
    if (selectedProjects.includes(projectId)) {
      setSelectedProjects(selectedProjects.filter((id) => id !== projectId));
    } else {
      setSelectedProjects([...selectedProjects, projectId]);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} sx={styles.dialogContainer}>
      <DialogTitle sx={styles.dialogTitle}>
        <Typography sx={styles.dialogTitleText}>Add Projects</Typography>
        <IconButton 
          onClick={onClose}
          sx={styles.dialogTitleCloseButton}
        >
          <CloseIcon sx={styles.dialogTitleCloseButtonIcon} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Typography variant="body1">Please select below the project(s) that you want to add to this folder:</Typography>
        <Box sx={styles.dialogContentProjectsContainer}>
          <List sx={styles.dialogContentProjectsList}>
            {loadingAvailableProjects ? (
              <Box sx={styles.dialogProgressContainer}>
                <CircularProgress size={40} sx={styles.dialogContentProjectsListProgress}/>
              </Box>
            ) : (
              availableProjects.map((project) => (
              <ListItem 
                key={project._id} 
                sx={styles.dialogContentProjectsListItem}
              >
                <NoteAddOutlinedIcon
                  sx={selectedProjects.includes(project._id) ? styles.dialogContentProjectsListItemIconSelected : styles.dialogContentProjectsListItemIcon}
                />
                <Tooltip title={project.name} arrow placement="top">
                  <Typography sx={styles.dialogContentProjectsListItemText}>{project.name}</Typography>
                </Tooltip>
                <Button
                  variant="text" 
                  color="primary" 
                  onClick={() => handleSelectProject(project._id)} 
                  sx={selectedProjects.includes(project._id) ? styles.dialogContentProjectsListItemButtonSelected : styles.dialogContentProjectsListItemButton}
                >
                  {selectedProjects.includes(project._id) ? 'Remove' : 'Add'}
                </Button>
              </ListItem>
            )))}
          </List>
        </Box>
        {moveFailed && (
          <Typography variant="body2" sx={styles.moveFailedText}>
            The projects could not be added. Reload the page and try again.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleMoveProjects} 
          disabled={loading || selectedProjects.length === 0}
          sx={styles.dialogActionsButton}
        >
          {
            loading ? (
              <CircularProgress size={20} sx={styles.dialogActionsButtonProgress} />
            ) : (
              'Add Projects'
            )
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveProjectBulkDialog;